import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Image, Modal, Spinner } from "react-bootstrap";
import { Badge, Avatar, Menu, MenuItem, Tabs, Tab } from "@mui/material";
import * as Endpoints from "../entities/Endpoints";
import {
  getNotificationDetails,
  getAnnouncementList,
  updateNotificationDetails,
  markAsRead,
  markAllAsRead,
} from "../redux/Slices/notificationSlice";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SettingsIcon from "@mui/icons-material/Settings";
import useCustomTranslation from "../hooks/useCustomTranslation";
import SignOutConfirmation from "./SignOutConfirmation";
import useSignalR from "./Common/useSignalR";
import { CONSTANTS } from "../utils/Constant/Constant";
import { formatDate } from "../utils/Utils";
import { toast } from "react-toastify";
import placeholderBackImage from "../styles/Assets/roarLogoAiFullColor.png";
import { Row, Col } from "antd";

const Header = ({ logout, setIsMessageListOpen }) => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { profileData, error } = useSelector((state) => state.profile);
  const {
    notifications,
    announcements,
    totalCount,
    announcementCount,
    loading,
    announcementsLoading,
  } = useSelector((state) => state.notifications);
  const coachId = profileData?.coachId;

  const notificationRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationList, setNotificationList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const openMenu = Boolean(anchorEl);

  useEffect(() => {
    if (coachId) {
      dispatch(getNotificationDetails({ coachId }));
      dispatch(getAnnouncementList({ coachId }));
    }
  }, [coachId, dispatch]);

  useEffect(() => {
    if (profileData?.status === "Inactive") {
      logout();
      toast.error(
        <div>
          <strong>{trans("Common.inactiveCoach")}</strong>
        </div>,
        {
          position: "top-center",
          zIndex: 9999,
          toastId: "error-toast",
          autoClose: CONSTANTS.TOAST_DURATION_LONG,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );
    }
    if (error?.status === 404) {
      logout();
      toast.error(
        <div>
          <strong>{error?.data?.message}</strong>
        </div>,
        {
          position: "top-center",
          zIndex: 9999,
          toastId: "error-toast",
          autoClose: CONSTANTS.TOAST_DURATION_LONG,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );
    }
  }, [profileData, error, trans, logout]);

  useSignalR(
    coachId,
    Endpoints.BASE_ENDPOINT + Endpoints.CHATHUB,
    CONSTANTS.RECEIVE_MESSAGE,
    getNotificationDetails,
    getAnnouncementList
  );

  useEffect(() => {
    setNotificationList(activeTab === 0 ? notifications : announcements);
  }, [notifications, announcements, activeTab]);

  const handleSettingsClick = () => {
    navigate("/settings");
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileSettingsClick = () => {
    navigate("/profile-settings");
    handleClose();
  };

  const handleProfileLogoutClick = () => {
    navigate("/login");
    logout();
    toast.success(
      <div>
        <strong>{trans("Common.success")}</strong>
        <p>{trans("Common.loggedOut")}</p>
      </div>,
      {
        position: "top-center",
        zIndex: 9999,
        toastId: "success-toast",
        autoClose: CONSTANTS.TOAST_DURATION_LONG,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
      }
    );
  };

  const handleSignOut = () => {
    setShowSignOutModal(true);
  };

  const handleNotificationClick = () => {
    setNotificationsOpen(!notificationsOpen);
  };

  const handleNotificationNavigation = (notification) => {
    if (!notification?.isRead) {
      dispatch(
        updateNotificationDetails({
          coachId,
          isMarkAllRead: false,
          notificationId: notification?.id,
        })
      );
      dispatch(markAsRead({ id: notification?.id }));
    }

    if (notification.type === CONSTANTS.CHECKIN) {
      navigate("/clients/clientDetails", {
        state: {
          client: notification.clientId,
          type: CONSTANTS.MEALS,
          isConversationEnabled: true,
        },
      });
      setNotificationsOpen(false);
    } else if (notification.type === CONSTANTS.PAYMENT) {
      navigate("/clients/clientDetails", {
        state: {
          client: notification.clientId,
          type: CONSTANTS.PAYMENT,
          isConversationEnabled: true,
        },
      });
      setNotificationsOpen(false);
    } else if (notification.type === "conversation") {
      setIsMessageListOpen(true);
      setNotificationsOpen(false);
    }
  };
  const handleAnnouncementNavigation = (notification) => {
    if (!notification?.isRead) {
      dispatch(
        updateNotificationDetails({
          coachId,
          isMarkAllRead: false,
          notificationId: notification?.notificationId,
        })
      );
      dispatch(
        markAsRead({ id: notification?.notificationId, isAnnouncement: true })
      );
    }
    try {
      const announcementObj = JSON.parse(notification.message);
      setSelectedAnnouncement({
        ...announcementObj,
        createdOn: notification.createDate,
      });
    } catch (e) {
      setSelectedAnnouncement({
        title: notification.title,
        announcement: notification.announcement,
        createdOn: notification.createDate,
        announcementDetails: notification.announcementDetails,
      });
    }
    setNotificationsOpen(false);
  };

  const handleMarkAllAsRead = () => {
    const notificationId = null;
    const coachId = profileData?.coachId;
    const isMarkAllRead = true;

    dispatch(
      updateNotificationDetails({ coachId, isMarkAllRead, notificationId })
    );
    dispatch(markAllAsRead());
    setNotificationsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationsOpen(false);
      }
    };

    if (notificationsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationsOpen]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setNotificationList(newValue === 0 ? notifications : announcements);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
  };

  const getFileIcon = (fileType) => {
    switch (fileType?.toLowerCase()) {
      case "pdf":
        return <i className="bi bi-file-earmark-pdf fs-3 text-danger"></i>;
      case "doc":
      case "docx":
        return <i className="bi bi-file-earmark-word fs-3 text-primary"></i>;
      case "xls":
      case "xlsx":
        return <i className="bi bi-file-earmark-excel fs-3 text-success"></i>;
      case "ppt":
      case "pptx":
        return <i className="bi bi-file-earmark-ppt fs-3 text-warning"></i>;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <i className="bi bi-file-earmark-image fs-3 text-info"></i>;
      default:
        return <i className="bi bi-file-earmark fs-3 text-secondary"></i>;
    }
  };

  return (
    <header className="header ">
      <SignOutConfirmation
        showModal={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onSignOut={handleProfileLogoutClick}
      />

      <Modal
        show={!!selectedAnnouncement}
        onHide={handleCloseAnnouncementModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedAnnouncement?.title || "Announcement Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="announcement-details">
            <div className="mb-3">
              <small className="text-muted">
                Posted on: {formatDate(selectedAnnouncement?.createdOn) || "-"}
              </small>
            </div>

            <div className="mb-4">
              <p className="mb-1 text-muted">Message:</p>
              <div className="p-3 bg-light rounded">
                {selectedAnnouncement?.announcement || "No details available"}
              </div>
            </div>

            {selectedAnnouncement?.announcementDetails?.url && (
              <div className="mb-3">
                <p className="mb-1 text-muted">Attachment:</p>
                <div className="d-flex align-items-center gap-3 p-3 bg-light rounded">
                  {getFileIcon(
                    selectedAnnouncement.announcementDetails.fileType
                  )}
                  <div>
                    <a
                      href={selectedAnnouncement.announcementDetails.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-block mb-1 text-decoration-none"
                    >
                      {selectedAnnouncement.announcementDetails.fileName ||
                        "Download File"}
                    </a>
                    <small className="text-muted">
                      {selectedAnnouncement.announcementDetails.fileType?.toUpperCase()}{" "}
                      {/* •
                      {selectedAnnouncement.announcementDetails.mediaType &&
                        ` ${selectedAnnouncement.announcementDetails.mediaType}`} */}
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseAnnouncementModal}
            className="saveButton"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Row className="w-100" align="middle" justify="space-between">
        <Col>
          <div className="d-flex align-items-center gap-5">
            <Image src={placeholderBackImage} className="header-logo" />
            <div>
              <h2 className="welcome-title ">
                Welcome{" "}
                <span className="coach-name">{profileData?.firstName}</span>
              </h2>
            </div>
          </div>
        </Col>

        {/* Right Side */}
        <Col>
          <div className="header-items d-flex align-items-center ">
            <div className="header-item" onClick={handleNotificationClick}>
              <Badge
                badgeContent={totalCount + announcementCount}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#dc3545",
                    color: "#f9f9f9",
                  },
                }}
              >
                <NotificationsIcon sx={{ color: "#383D8D" }} />
              </Badge>
            </div>

            <div className="header-item" onClick={handleSettingsClick}>
              <SettingsIcon sx={{ color: "#383D8D" }} />
              <span>{trans("Common.settings")}</span>
            </div>

            <div className="header-avatar" onClick={handleClick}>
              {profileData?.profilePicture?.url ? (
                <Image
                  src={`${profileData?.profilePicture?.url}`}
                  alt="Profile Picture"
                  className="header-avatar-logo rounded-circle"
                />
              ) : (
                <Avatar
                  alt={profileData?.firstName}
                  src="/path/to/avatar.jpg"
                  className="header-avatar-logo"
                />
              )}
            </div>
          </div>
        </Col>
      </Row>
      {notificationsOpen && (
        <div className="notifications-dropdown" ref={notificationRef}>
          <div className="notification-header">
            <h5>Notifications</h5>
            {notificationList.length > 0 && (
              <p
                onClick={handleMarkAllAsRead}
                className="text-color cursor-pointer"
              >
                Mark all as read
              </p>
            )}
          </div>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              label={`${
                totalCount > 0
                  ? `Notifications (${totalCount})`
                  : "Notifications"
              }`}
              className="fs-6 text-capitalize"
            />
            <Tab
              label={`${
                announcementCount > 0
                  ? `Announcements (${announcementCount})`
                  : "Announcements"
              }`}
              className="fs-6 text-capitalize"
            />
          </Tabs>

          <div className="notification-list">
            {(activeTab === 0 ? loading : announcementsLoading) ? (
              <div className="text-center py-3">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : notificationList.length === 0 ? (
              <p className="text-center py-3">
                No new {activeTab === 0 ? "notifications" : "announcements"}
              </p>
            ) : (
              notificationList.map((notification) => {
                const textClass = notification.isRead
                  ? "text-color-gray-40"
                  : "font-bold";

                return (
                  <div
                    key={notification.id}
                    className="notification-item"
                    onClick={() =>
                      activeTab === 0
                        ? handleNotificationNavigation(notification)
                        : handleAnnouncementNavigation(notification)
                    }
                  >
                    <Avatar
                      alt={notification?.type}
                      src={`${notification?.logoUrl || "/default-avatar.png"}`}
                      className="header-avatar-logo"
                    />
                    <div className="d-flex flex-column">
                      <p className={`pl-2 ${textClass}`}>
                        {notification?.title}
                      </p>
                      <p className={`pl-2 ${textClass}`}>
                        {notification?.createDate
                          ? formatDate(notification?.createDate)
                          : "-"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        className="mt-2"
      >
        <div className="m-3 ">
          <MenuItem className="p-2 menu-user-details-view ">
            {profileData?.logoImage?.url ? (
              <Image
                src={`${profileData?.logoImage?.url}`}
                alt="Profile Picture"
                className="user-avatar-logo rounded-circle"
              />
            ) : (
              <Avatar
                alt={profileData?.firstName}
                src="/path/to/avatar.jpg"
                className="header-avatar-logo"
              />
            )}
            <div className="d-flex flex-column app-font-proxima justify-content-center ml-3 ">
              <h5 className="mb-0">
                {profileData?.firstName + " " + profileData?.lastName || "-"}
              </h5>
              <p className="mb-0 text-muted fs-6">
                {profileData?.email || "-"}
              </p>
            </div>
          </MenuItem>

          <hr className="m-2" />

          <MenuItem onClick={handleProfileSettingsClick} className="m-0 p-1 ">
            <p className="app-font-proxima m-0 p-1 fs-6">
              {trans("Common.profile") + " " + trans("Common.settings")}
            </p>
          </MenuItem>

          <hr className="m-2" />
          <MenuItem onClick={handleClose} className="app-font-proxima m-0 p-1">
            <Button
              onClick={handleSignOut}
              className="p-3 w-100 saveButton d-flex align-items-center justify-content-center "
            >
              Sign Out
            </Button>
          </MenuItem>
        </div>
      </Menu>
    </header>
  );
};

export default Header;
